import { cssVars } from "../css-vars";
import { cn } from "../lib/cn";
import { CHART_TONES, defaultFormat, type ChartTone } from "./chart-base";

const SW = 100;
const SH = 28;

export interface SparklineProps {
  values: readonly number[];
  /** Spoken summary is built from this, e.g. "Asks over the last 7 days". */
  label: string;
  tone?: ChartTone;
  formatValue?: (value: number) => string;
  className?: string;
}

/** Tiny trend line; exposed as one image with a text summary (first, last, peak). */
export function Sparkline({ values, label, tone = "brand", formatValue = defaultFormat, className }: SparklineProps) {
  const max = Math.max(1, ...values);
  const step = SW / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(SH - 2 - (v / max) * (SH - 4)).toFixed(1)}`).join(" ");
  const first = values[0] ?? 0;
  const last = values.at(-1) ?? 0;
  const summary = `${label}: from ${formatValue(first)} to ${formatValue(last)}, peak ${formatValue(Math.max(0, ...values))}`;
  return (
    <svg role="img" aria-label={summary} viewBox={`0 0 ${SW} ${SH}`} preserveAspectRatio="none" className={cn("h-7 w-24 overflow-visible", className)}>
      <title>{summary}</title>
      <polyline points={points} fill="none" strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" className={CHART_TONES[tone].stroke} />
    </svg>
  );
}

export interface HorizontalBarItem {
  label: string;
  value: number;
  /** Visible value text (defaults to `${value}%` when `percent`). */
  display?: string;
}

export interface HorizontalBarListProps {
  items: readonly HorizontalBarItem[];
  "aria-label": string;
  /** Values are percentages of 100 (dashboard topics) instead of relative to the max. */
  percent?: boolean;
  tone?: ChartTone;
  className?: string;
}

/** "What people ask about": label + value text + flat bar (width via `--geo-width`). */
export function HorizontalBarList({ items, percent = true, tone = "brand", className, ...rest }: HorizontalBarListProps) {
  const max = percent ? 100 : Math.max(1, ...items.map((i) => i.value));
  return (
    <ul aria-label={rest["aria-label"]} className={cn("space-y-3.5", className)}>
      {items.map((item) => {
        const width = Math.min(Math.max((item.value / max) * 100, 0), 100);
        return (
          <li key={item.label}>
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-fg">{item.label}</span>
              <span className="tabular-nums text-fg-muted">{item.display ?? (percent ? `${item.value}%` : defaultFormat(item.value))}</span>
            </div>
            <div aria-hidden="true" className="mt-1.5 h-2 rounded-pill bg-sunken">
              <div className={cn("h-2 w-[var(--geo-width)] rounded-pill", CHART_TONES[tone].swatch)} style={cssVars({ "--geo-width": `${width}%` })} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
